import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    ExpenseClaimCreateRequest,
    ExpenseClaimDTO,
    ExpenseClaimLineRequest,
    ExpenseReceiptDTO,
    ReimbursementMethod,
} from '../interfaces/expense.interface';

interface PagedResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

export const ExpenseClaimService = {
    listMine: async (page = 0, size = 20): Promise<PagedResponse<ExpenseClaimDTO>> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.EXPENSE_CLAIM.LIST_MINE(institutionId), {
            params: { page, size },
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    getById: async (id: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.get(ENDPOINTS.EXPENSE_CLAIM.GET_BY_ID(id), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    createDraft: async (req: Omit<ExpenseClaimCreateRequest, 'institutionId'>): Promise<ExpenseClaimDTO> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.post(
            ENDPOINTS.EXPENSE_CLAIM.CREATE,
            { ...req, institutionId },
            { headers: AuthService.getAuthHeader() },
        );
        return response.data;
    },

    updateDraft: async (id: string, req: Omit<ExpenseClaimCreateRequest, 'institutionId'>): Promise<ExpenseClaimDTO> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.put(
            ENDPOINTS.EXPENSE_CLAIM.UPDATE(id),
            { ...req, institutionId },
            { headers: AuthService.getAuthHeader() },
        );
        return response.data;
    },

    submit: async (id: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.post(ENDPOINTS.EXPENSE_CLAIM.SUBMIT(id), null, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    recall: async (id: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.post(ENDPOINTS.EXPENSE_CLAIM.RECALL(id), null, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    softDelete: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.EXPENSE_CLAIM.DELETE(id), {
            headers: AuthService.getAuthHeader(),
        });
    },

    listPendingApproval: async (page = 0, size = 50): Promise<PagedResponse<ExpenseClaimDTO>> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.EXPENSE_CLAIM.PENDING_APPROVAL(institutionId), {
            params: { page, size },
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    approve: async (id: string, method?: ReimbursementMethod): Promise<ExpenseClaimDTO> => {
        const response = await axios.post(
            ENDPOINTS.EXPENSE_CLAIM.APPROVE(id),
            null,
            {
                params: method ? { method } : undefined,
                headers: AuthService.getAuthHeader(),
            },
        );
        return response.data;
    },

    reject: async (id: string, reason: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.post(
            ENDPOINTS.EXPENSE_CLAIM.REJECT(id),
            { reason },
            { headers: AuthService.getAuthHeader() },
        );
        return response.data;
    },

    editLine: async (id: string, lineId: string, req: ExpenseClaimLineRequest): Promise<ExpenseClaimDTO> => {
        const response = await axios.put(
            ENDPOINTS.EXPENSE_CLAIM.EDIT_LINE(id, lineId),
            req,
            { headers: AuthService.getAuthHeader() },
        );
        return response.data;
    },

    removeLine: async (id: string, lineId: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.delete(ENDPOINTS.EXPENSE_CLAIM.REMOVE_LINE(id, lineId), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    payStandalone: async (id: string, cashOrBankAccountId: string, paymentReference: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.post(
            ENDPOINTS.EXPENSE_CLAIM.PAY_STANDALONE(id),
            { cashOrBankAccountId, paymentReference },
            { headers: AuthService.getAuthHeader() },
        );
        return response.data;
    },

    linkAdvance: async (id: string, advanceId: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.post(
            ENDPOINTS.EXPENSE_CLAIM.LINK_ADVANCE(id, advanceId),
            null,
            { headers: AuthService.getAuthHeader() },
        );
        return response.data;
    },

    listAll: async (status?: string, employeeUserId?: string, page = 0, size = 50): Promise<PagedResponse<ExpenseClaimDTO>> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const params: Record<string, string | number> = { page, size };
        if (status) params.status = status;
        if (employeeUserId) params.employeeUserId = employeeUserId;
        const response = await axios.get(ENDPOINTS.EXPENSE_CLAIM.LIST_ALL(institutionId), {
            params,
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    unlinkAdvance: async (id: string): Promise<ExpenseClaimDTO> => {
        const response = await axios.delete(ENDPOINTS.EXPENSE_CLAIM.UNLINK_ADVANCE(id), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },
    // ---- receipts (a draft's owner uploads/removes; anyone who may open the claim downloads) ----
    listReceipts: async (id: string): Promise<ExpenseReceiptDTO[]> => {
        const response = await axios.get(ENDPOINTS.EXPENSE_CLAIM.RECEIPTS(id), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    uploadReceipt: async (id: string, file: File): Promise<ExpenseReceiptDTO> => {
        const form = new FormData();
        form.append('file', file);
        const response = await axios.post(ENDPOINTS.EXPENSE_CLAIM.RECEIPTS(id), form, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    removeReceipt: async (id: string, attachmentId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.EXPENSE_CLAIM.RECEIPT(id, attachmentId), {
            headers: AuthService.getAuthHeader(),
        });
    },

    downloadReceipt: async (id: string, attachmentId: string): Promise<{ blob: Blob; fileName: string }> => {
        const response = await axios.get(ENDPOINTS.EXPENSE_CLAIM.RECEIPT(id, attachmentId), {
            headers: AuthService.getAuthHeader(),
            responseType: 'blob',
        });
        const disposition: string = response.headers?.['content-disposition'] ?? '';
        const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
        const plain = /filename="?([^";]+)"?/i.exec(disposition);
        let fileName = plain ? plain[1] : 'receipt';
        if (star) {
            try {
                fileName = decodeURIComponent(star[1]);
            } catch {
                // keep the plain name
            }
        }
        return { blob: response.data, fileName };
    },
};
